import { cn } from "../../../lib/utils";
import { lpConfig } from "../lpConfig";
import { useFadeIn } from "../useFadeIn";

/**
 * 評価記号をレンダリングする
 * @param {string} rating - 評価値
 * @param {boolean} isStrix - StrixSearchカラムかどうか
 * @return {JSX.Element} 評価表示要素
 */
function RatingCell({ rating, isStrix }: { rating: string; isStrix: boolean }) {
  if (rating === "excellent") {
    return <span className="text-[#30BF78] font-bold">◎</span>;
  }
  if (rating === "good") {
    return <span className="text-[#ff8d00]">△</span>;
  }
  if (rating === "poor") {
    return <span className="text-red-400">×</span>;
  }
  // Custom text (e.g., prices, "◎(即日)")
  if (rating.includes("◎")) {
    return <span className="text-[#30BF78] font-bold">{rating}</span>;
  }
  return (
    <span className={isStrix ? "text-white font-bold" : "text-gray-700"}>
      {rating}
    </span>
  );
}

/**
 * 他ツールとの機能比較テーブルセクション
 * StrixSearchと他ツール（Excel管理・汎用CRM・kintone）を機能ごとに比較表示する
 * @return {JSX.Element} 比較テーブルセクションコンポーネント
 */
export function LpComparisonTable() {
  const { ref, isVisible } = useFadeIn();

  return (
    <section className="py-24 lg:py-32 bg-white">
      <div
        ref={ref}
        className={cn(
          "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 lp-fade-in-up",
          isVisible && "lp-visible",
        )}
      >
        <div className="text-center">
          <p className="text-xs font-bold tracking-widest text-[#ff8d00] uppercase mb-4">
            {lpConfig.comparisonTable.sectionLabel}
          </p>
          <h2 className="text-4xl font-bold text-[#0F1649] mb-16">
            {lpConfig.comparisonTable.heading}
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full rounded-2xl overflow-hidden border border-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500">
                  機能
                </th>
                {lpConfig.comparisonTable.competitors.map(
                  (competitor, index) =>
                    index === 0 ? (
                      <th
                        key={competitor}
                        className="py-4 px-6 text-center bg-[#02072F] text-white font-bold"
                      >
                        <div className="flex flex-col items-center gap-1">
                          <span className="inline-block px-2 py-0.5 text-[10px] rounded-full bg-[#ff8d00] text-white font-bold">
                            おすすめ
                          </span>
                          {competitor}
                        </div>
                      </th>
                    ) : (
                      <th
                        key={competitor}
                        className="py-4 px-6 text-center text-sm font-medium text-gray-500"
                      >
                        {competitor}
                      </th>
                    ),
                )}
              </tr>
            </thead>
            <tbody>
              {lpConfig.comparisonTable.rows.map((row, index) => (
                <tr
                  key={row.feature}
                  className={cn(
                    "border-t border-gray-200",
                    index % 2 === 1 && "bg-gray-50/50",
                  )}
                >
                  <td className="py-4 px-6 text-sm font-medium text-gray-700">
                    {row.feature}
                  </td>
                  {row.ratings.map((rating, ratingIndex) => (
                    <td
                      key={`${row.feature}-${ratingIndex}`}
                      className={cn(
                        "py-4 px-6 text-center",
                        ratingIndex === 0 && "bg-[#02072F]/5",
                      )}
                    >
                      <RatingCell rating={rating} isStrix={ratingIndex === 0} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
